import { skipToken, useMutation, useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { twoFactorApi } from '../../services/api/twoFactor.api'
import type { ChallengeCodePayload, DisablePayload, EnrollPayload } from '../../app/modules/types/twoFactor'

/* ------------------------------------------------------- sign-in challenge */

/** Enrolled user: the code (or a recovery code) completes sign-in. The caller routes on success. */
export function useVerifyChallenge() {
  return useMutation({ mutationFn: (payload: ChallengeCodePayload) => twoFactorApi.verifyChallenge(payload) })
}

/**
 * Fetch the secret and QR for a user who must enrol.
 *
 * A query rather than a mutation although it is a POST: it fires when the setup
 * screen MOUNTS, and a mutation started from a mount effect loses its result
 * when StrictMode remounts the page mid-request, leaving the screen on its
 * skeleton. It is held to exactly one request per token — no refetching —
 * because every call mints a new secret; `refetch()` is the explicit "start
 * over". Nothing is kept once the screen unmounts.
 */
export function useChallengeEnrollment(challengeToken: string | null) {
  return useQuery({
    queryKey: queryKeys.twoFactor.challengeEnrollment,
    queryFn: challengeToken === null ? skipToken : () => twoFactorApi.enrollChallenge(challengeToken),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

/** Confirm the first code: enrolled AND signed in; returns the recovery codes once. */
export function useConfirmChallengeEnrollment() {
  return useMutation({
    mutationFn: (payload: ChallengeCodePayload) => twoFactorApi.confirmChallengeEnrollment(payload),
  })
}

/* ---------------------------------------------------------- account (self) */

export function useTwoFactorStatus() {
  return useQuery({
    queryKey: queryKeys.twoFactor.status,
    queryFn: () => twoFactorApi.status(),
    retry: (count, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      return !(status && status >= 400 && status < 500) && count < 1
    },
  })
}

const refreshStatus = () => queryClient.invalidateQueries({ queryKey: queryKeys.twoFactor.status })

/** Enable (`{password}`) or rotate (`{password, code}`). The old authenticator keeps working until confirmed. */
export function useEnrollTwoFactor() {
  return useMutation({ mutationFn: (payload: EnrollPayload) => twoFactorApi.enroll(payload) })
}

export function useConfirmTwoFactor() {
  return useMutation({
    mutationFn: (code: string) => twoFactorApi.confirmEnrollment(code),
    onSuccess: refreshStatus,
  })
}

export function useRegenerateRecoveryCodes() {
  return useMutation({
    mutationFn: (code: string) => twoFactorApi.regenerateRecoveryCodes(code),
    onSuccess: refreshStatus,
  })
}

export function useDisableTwoFactor() {
  return useMutation({
    mutationFn: (payload: DisablePayload) => twoFactorApi.disable(payload),
    onSuccess: refreshStatus,
  })
}
